import { useCallback, useEffect, useState } from 'react'

import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { ContentTypeAppLabels } from '@webapp/core/entities/internal/enums/ContentTypeAppLabelEnum'
import { ContentTypeModels } from '@webapp/core/entities/internal/enums/ContentTypeModelEnum'
import {
  makeSelectContentTypesByModel,
  makeSelectFeatureSwitches,
  makeSelectUser,
} from '@webapp/core/stores/global'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { useDuplicateExpenseReportPresenter } from '@webapp/modules/expense/features/duplicate-expense-report/presenters/DuplicateExpenseReportPresenter'
import { useExpensePresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpensePresenter'
import { expenseReportQueryConfig } from '@webapp/modules/expense/query-configs'

interface UseExpenseReportEditViewParams {
  uuid: string
}

export const useExpenseReportEditView = ({
  uuid,
}: UseExpenseReportEditViewParams) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const featureSwitches = useSelector(makeSelectFeatureSwitches())
  const currentUser = useSelector(makeSelectUser())

  const { expense, isLoading } = useExpensePresenter({ uuid })
  const [editingItem, setEditingItem] = useState<Nullable<ExpenseItem>>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (expense && !expense.permissions.canEdit) {
      history.replace(`/expenses/${uuid}`)
    }
  }, [expense, uuid, history])

  const invalidateDetailQuery = useCallback(() => {
    queryClient.invalidateQueries(
      expenseReportQueryConfig.detail(uuid).queryKey
    )
  }, [queryClient, uuid])

  const handleDuplicateSuccess = useCallback(() => {
    window.location.href = '/#/expenses/request'
  }, [])

  const {
    commands: { duplicate: _duplicate },
  } = useDuplicateExpenseReportPresenter({
    expenseId: expense?.id ?? 0,
    onSuccess: handleDuplicateSuccess,
  })

  const expenseContentType = useSelector(
    makeSelectContentTypesByModel(
      ContentTypeAppLabels.PROCURIFY,
      ContentTypeModels.EXPENSEREPORT
    )
  )
  const expenseContentTypeId = expenseContentType?.id ?? 0

  const disableComments =
    !!featureSwitches.disable_comments_on_request_and_approve

  const isRequester = expense ? expense.requester.id === currentUser?.id : false
  const showComments = !disableComments && !!expense

  const handleLineItemEditClick = useCallback((item: ExpenseItem) => {
    setEditingItem(item)
    setDrawerOpen(true)
  }, [])

  const handleLineItemEditSuccess = useCallback(() => {
    invalidateDetailQuery()
    setEditingItem(null)
    setDrawerOpen(false)
  }, [invalidateDetailQuery])

  const handleLineItemEditCancel = useCallback(() => {
    setEditingItem(null)
    setDrawerOpen(false)
  }, [])

  const handleEditSuccess = useCallback(() => {
    history.push(`/expenses/${uuid}`)
  }, [history, uuid])

  const handleEditCancel = useCallback(() => {
    history.push(`/expenses/${uuid}`)
  }, [history, uuid])

  const duplicateExpense = async () => {
    if (!expense) return
    await _duplicate()
  }

  return {
    data: {
      expense,
      isLoading,
      editingItem,
      drawerOpen,
      isSubmitting,
    },
    states: {
      isRequester,
      showComments,
      expenseContentTypeId,
      userId: currentUser?.id ?? 0,
    },
    methods: {
      onLineItemEditClick: handleLineItemEditClick,
      onLineItemEditSuccess: handleLineItemEditSuccess,
      onLineItemEditCancel: handleLineItemEditCancel,
      onEditSuccess: handleEditSuccess,
      onEditCancel: handleEditCancel,
      onSubmittingChange: setIsSubmitting,
    },
    commands: {
      duplicateExpense,
    },
  }
}
